"use client";

import type { MuxPlayerRefAttributes } from "@mux/mux-player-react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { AnimationVideo } from "@/components/animation-video";
import { useIntersectedOnce } from "@/hooks/use-intersection-once";
import { lg, useMediaMinWidth } from "@/hooks/use-media-min-width";
import rootLogger from "@/lib/logging";
import { cn } from "@/lib/utils";

import { MuxVideo } from "../../post-rich-text/mux-video";

const moduleLogger = rootLogger.child({
  module: "custom-card/shared/video-player",
});

interface VideoAsset {
  playbackId: string;
  resolution: string;
  aspectRatio?: string | undefined;
}

export interface VideoPlayerHandle {
  play: () => void;
}

interface VideoPlayerProps {
  asset: VideoAsset;
  mobileAsset?: VideoAsset;
  animation: boolean | null;
  className?: string;
  /** Controls AnimationVideo loading strategy. Default "lazy" shows poster until in view. */
  loading?: "eager" | "lazy";
  loop?: boolean;
  autoPlay?: boolean;
  onEnded?: () => void;
}

export const VideoPlayer = forwardRef<VideoPlayerHandle, VideoPlayerProps>(
  (
    {
      asset,
      mobileAsset,
      animation,
      className,
      loading = "lazy",
      loop,
      autoPlay,
      onEnded,
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const htmlVideoRef = useRef<HTMLVideoElement | null>(null);
    const muxRef = useRef<MuxPlayerRefAttributes | null>(null);
    const [shouldPlay, setShouldPlay] = useState(false);

    const isInView = useIntersectedOnce(containerRef, {
      root: null,
      rootMargin: "0px",
      threshold: 0.3,
    });

    const isDesktop = useMediaMinWidth(lg, true);

    // Render only the active breakpoint's asset to avoid duplicate WebMediaPlayers
    const activeAsset = !isDesktop && mobileAsset ? mobileAsset : asset;

    useImperativeHandle(
      ref,
      () => ({
        play: () =>
          // Stage for playback until in view
          setShouldPlay(true),
      }),
      []
    );

    // Auto play when element enters view if autoPlay is not explicitly false,
    // or if an external play was requested earlier (shouldPlay)
    useEffect(() => {
      if (!isInView) return;
      if (autoPlay !== false || shouldPlay) {
        if (htmlVideoRef.current) {
          htmlVideoRef.current
            .play()
            .catch(() =>
              moduleLogger.warn(
                { playbackId: activeAsset.playbackId },
                "native video play method failed"
              )
            );

          setShouldPlay(false);
          return;
        }

        if (muxRef.current) {
          muxRef.current
            .play()
            .catch(() =>
              moduleLogger.warn(
                { playbackId: activeAsset.playbackId },
                "mux player play method failed"
              )
            );
          setShouldPlay(false);
          return;
        }
      }
    }, [isInView, autoPlay, shouldPlay, activeAsset.playbackId]);

    const videoProps = {
      title: "Video",
      playbackId: activeAsset.playbackId ?? "",
      aspectRatio: activeAsset.aspectRatio ?? "16:9",
      thumbnailTime: null,
      autoplay: null,
      cssVars: null,
    };

    const commonClassName = "overflow-hidden object-cover";

    // Gate autoplay until in view. Default to true if undefined when visible.
    const gatedAutoPlay = isInView ? (autoPlay ?? true) : false;

    return (
      <div ref={containerRef}>
        {animation ? (
          <AnimationVideo
            className={cn(commonClassName, className)}
            loading={loading}
            video={activeAsset}
            loop={loop ?? true}
            ref={htmlVideoRef}
            autoPlay={gatedAutoPlay}
            onEnded={() => onEnded?.()}
            thumbnailTime={videoProps.thumbnailTime}
          />
        ) : (
          <MuxVideo
            className={cn(commonClassName, className)}
            ref={muxRef}
            autoPlay={gatedAutoPlay}
            loop={loop ?? null}
            onEnded={() => onEnded?.()}
            {...videoProps}
          />
        )}
      </div>
    );
  }
);

VideoPlayer.displayName = "VideoPlayer";
